import React, { useState } from "react";
import axios from "axios";
import { ArrowUp, Check, Download, FileText, History, MessageSquare, X } from "lucide-react";
import { DocumentSections } from "@/components/documents/DocumentSections";
import { docStatusMeta, fmtDocDate } from "@/lib/docStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const m = docStatusMeta(status);
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border ${m.light}`}
      data-testid="client-doc-status"
      data-status={status}
    >
      {m.label}
    </span>
  );
}

function DocCard({ d, onOpen }) {
  const comments = d.comments || [];
  return (
    <button
      type="button"
      onClick={onOpen}
      className="bnm-card p-6 text-left hover:shadow-md transition-shadow group w-full"
      data-testid={`client-doc-card-${d.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <FileText className="w-4 h-4 text-muted-ink group-hover:text-ink transition-colors shrink-0 mt-0.5" />
        <StatusBadge status={d.doc_status} />
      </div>
      <div className="mt-4 text-[10.5px] uppercase tracking-[0.12em] text-muted-ink">{d.label}</div>
      <div className="mt-1.5 font-display font-bold text-[16.5px] leading-[1.3] text-ink">{d.title}</div>
      <div className="mt-3 text-[11.5px] text-muted-ink leading-[1.6]">
        Shared {fmtDocDate(d.shared_at)}
        {d.updated_at && d.updated_at !== d.shared_at ? ` · Updated ${fmtDocDate(d.updated_at)}` : ""}
        {comments.length > 0 ? ` · ${comments.length} comment${comments.length > 1 ? "s" : ""}` : ""}
      </div>
    </button>
  );
}

function ClientComments({ d, projectId, onProject }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const comments = d.comments || [];

  const refresh = async () => {
    const { data } = await axios.get(`${API}/projects/${projectId}`);
    onProject(data.project);
  };

  const send = async () => {
    const t = text.trim();
    if (t.length < 1 || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/projects/${projectId}/documents/${d.id}/comments`, { text: t });
      setText("");
      await refresh();
    } catch (e) {
      console.error("comment failed", e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-[#eee]" data-testid="client-doc-comments">
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-muted-ink mb-4">
        <MessageSquare className="w-3.5 h-3.5" /> Your notes on this document
      </div>
      {comments.length === 0 && (
        <p className="text-[13px] text-muted-ink italic">
          Thoughts, questions, tweaks. Write them here and your Producer sees them instantly.
        </p>
      )}
      <div className="space-y-3">
        {comments.map((c) => (
          <div
            key={c.id}
            className={`rounded-xl px-4 py-3 border ${
              c.author === "producer" ? "bg-[#f5f7fb] border-[#e2e8f2]" : "bg-white border-[#eee]"
            }`}
            data-testid="client-doc-comment"
            data-author={c.author}
          >
            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-ink mb-1">
              {c.author === "producer" ? "Your Producer" : "You"} · {fmtDocDate(c.created_at)}
            </div>
            <p className="text-[13.5px] leading-[1.65] text-ink">{c.text}</p>
          </div>
        ))}
      </div>
      <div className="relative mt-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Comment on this document…"
          className="w-full rounded-xl border border-[#e0e0de] px-4 py-3 pr-12 text-[13.5px] text-ink leading-[1.6] resize-none focus:outline-none focus:border-ink"
          data-testid="client-doc-comment-input"
        />
        <button
          type="button"
          onClick={send}
          disabled={text.trim().length < 1 || sending}
          className="absolute right-2.5 bottom-3 w-8 h-8 rounded-full bg-[#0a0a0a] text-white flex items-center justify-center disabled:opacity-30"
          data-testid="client-doc-comment-send"
          aria-label="Send comment"
        >
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function ClientDocumentModal({ d, projectId, onProject, onClose }) {
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesNote, setChangesNote] = useState("");
  const [busy, setBusy] = useState(false);
  const doc = d.document || {};
  const history = d.history || [];
  const decided = d.doc_status === "approved" || d.doc_status === "final";

  const setStatus = async (status, note) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/documents/${d.id}/status`,
        { status, note: note || null },
      );
      onProject(data.project);
      setChangesOpen(false);
      setChangesNote("");
    } catch (e) {
      console.error("document status failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-full flex items-start justify-center px-4 py-10" onClick={(e) => e.stopPropagation()}>
        <div className="w-full max-w-[760px] rounded-2xl bg-white text-ink shadow-2xl overflow-hidden" data-testid="client-document">
          {/* Branded header */}
          <div className="bg-[#0a0a0a] px-8 py-5 flex items-center justify-between">
            <div>
              <span className="font-display font-bold text-white text-[13px] tracking-[0.03em]">BRAND NEW MONEY</span>
              <span className="font-serif-italic font-light text-white/80 text-[12px]"> Films</span>
              <span className="text-[#E53935] text-[11px]"> &#9679;</span>
              <span className="ml-3 text-white/40 text-[10px] uppercase tracking-[0.14em]">{d.label}</span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href={`${API}/projects/${projectId}/documents/${d.id}/pdf`}
                className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] transition-colors"
                data-testid="client-doc-pdf"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </a>
              <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="client-doc-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="px-8 md:px-12 py-10">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <StatusBadge status={d.doc_status} />
              <span className="text-[11.5px] text-muted-ink" data-testid="client-doc-dates">
                Shared {fmtDocDate(d.shared_at)}
                {d.updated_at && d.updated_at !== d.shared_at ? ` · Updated ${fmtDocDate(d.updated_at)}` : ""}
              </span>
            </div>

            <h2 className="font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.03em] leading-[1.05]">
              {doc.title || d.title}
            </h2>
            {doc.subtitle && (
              <p className="mt-3 font-serif-italic text-muted-ink text-[16px] md:text-[18px] leading-[1.5]">{doc.subtitle}</p>
            )}

            <div className="mt-10">
              <DocumentSections sections={doc.sections} />
            </div>

            {!decided && (
              <div className="mt-12 pt-8 border-t border-[#eee]" data-testid="client-doc-actions">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setStatus("approved")}
                    disabled={busy}
                    className="btn-denim disabled:opacity-50 justify-center"
                    data-testid="client-doc-approve"
                  >
                    <Check className="w-4 h-4" />
                    <span>{busy ? "One moment…" : "Approve this document"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setChangesOpen((v) => !v)}
                    className="btn-ghost justify-center"
                    data-testid="client-doc-request-changes"
                  >
                    Request changes
                  </button>
                </div>
                {changesOpen && (
                  <div className="mt-4">
                    <textarea
                      value={changesNote}
                      onChange={(e) => setChangesNote(e.target.value)}
                      rows={3}
                      autoFocus
                      placeholder="What would you like changed? Your Producer reads every word."
                      className="w-full rounded-xl border border-[#e0e0de] px-4 py-3 text-[13.5px] text-ink leading-[1.6] resize-none focus:outline-none focus:border-ink"
                      data-testid="client-doc-changes-note"
                    />
                    <button
                      type="button"
                      onClick={() => setStatus("needs_changes", changesNote.trim())}
                      disabled={busy}
                      className="mt-2 inline-flex items-center gap-1.5 text-[13px] px-5 py-2 rounded-full bg-[#0a0a0a] text-white disabled:opacity-40"
                      data-testid="client-doc-changes-send"
                    >
                      {busy ? "Sending…" : "Send to your Producer"}
                    </button>
                  </div>
                )}
              </div>
            )}
            {d.doc_status === "approved" && (
              <p className="mt-12 pt-8 border-t border-[#eee] text-[13px] text-[#1c7c3c]" data-testid="client-doc-approved-line">
                You approved this document. Your Producer has been notified.
              </p>
            )}

            {history.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#eee]" data-testid="client-doc-history">
                <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-muted-ink mb-4">
                  <History className="w-3.5 h-3.5" /> Version history
                </div>
                <div className="space-y-2">
                  {[...history].reverse().map((h) => (
                    <div key={h.id} className="flex items-baseline justify-between gap-4 text-[12.5px]">
                      <span className="text-ink">{h.label}</span>
                      <span className="text-muted-ink shrink-0">{fmtDocDate(h.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ClientComments d={d} projectId={projectId} onProject={onProject} />

            <p className="mt-12 pt-6 border-t border-[#eee] text-[11px] text-muted-ink tracking-[0.02em]">
              Made in India. Producing worldwide. · Prepared for you by Brand New Money Films
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// The premium collaboration shelf: every document your Producer shares, in one place.
export default function ClientDocuments({ project, onProject }) {
  const docs = project.documents || [];
  const [openId, setOpenId] = useState(null);
  if (docs.length === 0) return null;
  const open = docs.find((d) => d.id === openId);

  return (
    <div className="mt-16 md:mt-20 max-w-[720px] mx-auto text-left" data-testid="client-documents">
      <div className="eyebrow eyebrow-denim mb-3 text-center">From Your Producer</div>
      <p className="text-center text-[13.5px] text-muted-ink mb-8 leading-[1.6]">
        Documents prepared for this project. Review, comment and approve, all in one place.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        {[...docs].reverse().map((d) => (
          <DocCard key={d.id} d={d} onOpen={() => setOpenId(d.id)} />
        ))}
      </div>
      {open && (
        <ClientDocumentModal
          d={open}
          projectId={project.id}
          onProject={onProject}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
